/**
 * Fortschrittskarte „Entwicklung“ oben im Chat (3.11.2026, Martin: „kann man das nicht einfach einblenden lassen?“).
 *
 * Wie die To-do-Liste in Claude Desktop: eine schmale, zugeklappte Karte mit EINER Zeile („Entwicklung  3 im Bau“), aufklappbar zu
 * einer Liste — je Arbeit ein Zeichen für den Stand und ihr Titel —, darunter EINE Zeile mit dem aktuellen Schritt und der Laufzeit
 * („Prüfung läuft, 12 Min.“). Sie erscheint von selbst, solange Arbeit läuft, und verschwindet, wenn nichts mehr läuft.
 *
 * Nichts Neues am Server: Quelle ist die vorhandene Tafel der Entwicklung (server/schmied-arbeit.ts, Ereignis `schmied-arbeit` auf dem
 * Chat-Strom, shared/schmied-arbeit.ts) — dieselben Titel, dieselben Stufen, derselbe Schutz: nur Konto 1, nur mit dem Kontoschalter
 * „Entwickler im Chat“, Titel am Server von Pfaden, Hashes, Geheimnissen und Privatem gereinigt. Keine neue Einstellung.
 *
 * Stand → Zeichen (Form, nie nur Farbe; die Zeichnungen stehen in lib/zeichen.ts):
 *   fertig                          → erledigt  (voller Kreis mit Haken)
 *   baut, testet, fädelt ein, prüft → läuft     (Ring mit drehendem Bogen; mit „Bewegung reduzieren“ ein ruhiger Kern im Ring)
 *   wartet auf dich                 → offen     (gestrichelter Ring)
 *
 * ⚠️ Die Marke „liest Privates mit“ (Kennung SCHMIED_MARKE_ID) ist keine Arbeit und steht hier nicht.
 * ⚠️ Texte nur über textContent; die Zeichen sind feste Zeichnungen aus dem Vorrat. Keine Inline-Styles (style.css, `.entw-…`).
 * ⚠️ Drosselung: ein neuer Stand wird höchstens einmal je Sekunde gezeichnet (der Rest wird zusammengefasst); die Laufzeit tickt alle
 *    30 s und nur bei sichtbarer Seite; die Liste wird nur neu gebaut, wenn sich Titel oder Stand geändert haben.
 * ⚠️ Barrierefreiheit: Bereich mit Beschriftung; Kopf ist ein Knopf (aria-expanded, aria-controls); der Stand jeder Zeile steht für
 *    Screenreader als Wort davor („baut: …“), die Zeichen sind verborgen; der sichtbare Satz tickt und ist darum NICHT live — eine
 *    eigene, unsichtbare Ansage sagt nur eine Änderung an (aria-live="polite"), nicht den ersten Stand beim Laden.
 */
import {
  SCHMIED_ARBEIT_HOECHSTENS,
  SCHMIED_ARBEIT_ORT_NAME,
  SCHMIED_ARBEIT_SCHRITT_NAME,
  SCHMIED_ARBEIT_STUFEN,
  SCHMIED_ARBEIT_STUFE_NAME,
  SCHMIED_ARBEIT_VERFALL_MS,
  SCHMIED_MARKE_ID,
  istAktiv,
  type SchmiedArbeitStand,
  type SchmiedArbeitStufe,
} from "../../shared/schmied-arbeit.js";
import { dauerText } from "./schmied-arbeit.js";
import { zeichen, zustandZeichen } from "./zeichen.js";

export const ENTWICKLUNG_KARTE_ID = "entwicklung-karte";
const FELD_ID = "entwicklung-karte-feld";
/** Höchstens so oft wird ein neuer Stand gezeichnet (ms). */
export const KARTE_DROSSEL_MS = 1000;
/** Die Laufzeit rückt so oft vor (ms) — und nur bei sichtbarer Seite. */
export const KARTE_TAKT_MS = 30_000;
/** So lange nach einer neuen Verbindung darf der Server den Stand nachliefern, bevor die Karte ihn als beendet nimmt (ms). */
export const KARTE_NACHLIEFERUNG_MS = 3000;
/** Mehr verschiedene Stände (mit Wartenden: mehr als einer) nennt die Kurzzeile nicht einzeln: dann „N laufen“. */
const KURZZEILE_ARTEN = 2;
const ZEICHEN_GROESSE = 16;

export type KartenZustand = "erledigt" | "laeuft" | "offen";

/** Eine Arbeit, wie die Karte sie führt; `start` nach der Uhr dieser Seite. */
export interface Posten {
  id: string;
  stichwort: string;
  stufe: SchmiedArbeitStufe;
  start: number;
  /** Nur „fertig“: die ganze Dauer (wächst nicht mehr). */
  dauerMs?: number;
}

/** erledigt / läuft / offen — „wartet auf dich“ ist die offene Sache. */
export function kartenZustand(stufe: SchmiedArbeitStufe): KartenZustand {
  if (stufe === "fertig") return "erledigt";
  return stufe === "wartet" ? "offen" : "laeuft";
}

/**
 * Die Kurzzeile im zugeklappten Zustand: „3 im Bau“, „1 im Bau, 1 in Prüfung“, „5 laufen, 1 wartet auf dich“; „“ wenn nichts läuft.
 * Wer auf Martin wartet, steht immer eigens dabei. Rein; nur Zahlen und feste Wörter.
 */
export function kurzzeile(stufen: readonly SchmiedArbeitStufe[]): string {
  const teile: string[] = [];
  const lauf: { n: number; ort: string }[] = [];
  for (const st of SCHMIED_ARBEIT_STUFEN) {
    const ort = SCHMIED_ARBEIT_ORT_NAME[st];
    const n = stufen.filter((x) => x === st).length;
    if (ort && n > 0) lauf.push({ n, ort });
  }
  const summe = lauf.reduce((a, l) => a + l.n, 0);
  const wartet = stufen.filter((x) => x === "wartet").length;
  // Höchstens zwei Teile, damit die Zeile schmal bleibt: neben „wartet auf dich“ darf nur EIN Stand einzeln stehen.
  const einzeln = lauf.length <= (wartet > 0 ? 1 : KURZZEILE_ARTEN);
  if (lauf.length > 0 && einzeln) teile.push(...lauf.map((l) => `${l.n} ${l.ort}`));
  else if (summe > 0) teile.push(`${summe} laufen`);
  if (wartet > 0) teile.push(`${wartet} ${wartet === 1 ? "wartet" : "warten"} auf dich`);
  return teile.join(", ");
}

/** „12 Min.“, „1 Std 5 Min.“, „unter 1 Min.“ — die Laufzeit als Satzende. */
export function laufzeitSatz(ms: number): string {
  return `${dauerText(ms).replace(/^<1/, "unter 1")}.`;
}

/**
 * Reihenfolge der Zeilen: laufende und wartende nach Beginn (wer zuerst anfing, steht oben), „fertig“ zuletzt — nach Kennung, NIE nach
 * Dauer oder Beginn. Der Beginn einer fertigen Arbeit ist nur „jetzt minus ihre Dauer“ und wandert mit jedem neuen Stand nach hinten;
 * wer danach sortiert, vertauscht fertige und laufende Zeilen von selbst (gemessen: fertig 10 Min., läuft 5 Min. — nach 6 Min. getauscht).
 * So springt nichts außer dem einen Mal, wenn eine Arbeit fertig wird und ans Ende rückt.
 */
export function ordneZeilen<T extends Pick<Posten, "id" | "start" | "stufe">>(liste: readonly T[]): T[] {
  const rang = (p: T): number => (istAktiv(p.stufe) ? 0 : 1);
  const nachKennung = (a: T, b: T): number => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  return [...liste].sort((a, b) => rang(a) - rang(b) || (rang(a) === 0 ? a.start - b.start : 0) || nachKennung(a, b));
}

/**
 * Die Zeile unter der Liste: der aktuelle Schritt und seine Laufzeit, „Prüfung läuft, 12 Min.“. Wer auf Martin wartet geht vor,
 * sonst die am längsten laufende Arbeit. „“ wenn nichts läuft.
 */
export function schrittSatz(liste: readonly Posten[], jetzt: number): string {
  const aktiv = ordneZeilen(liste.filter((p) => istAktiv(p.stufe)));
  const dran = aktiv.find((p) => p.stufe === "wartet") ?? aktiv[0];
  return dran ? `${SCHMIED_ARBEIT_SCHRITT_NAME[dran.stufe]}, ${laufzeitSatz(jetzt - dran.start)}` : "";
}

export interface EntwicklungKarte {
  /** Den Stand aus dem Strom zeigen (gedrosselt). */
  zeige(stand: SchmiedArbeitStand): void;
  /** Der Strom hat sich neu verbunden: der Server liefert einen noch laufenden Stand gleich nach, ein beendeter bleibt nicht hängen. */
  verbindungNeu(): void;
  /** Zeitgeber und Hörer aufräumen. */
  stopp(): void;
}

/** Die Bausteine der Karte (nur Gerüst, noch ohne Inhalt und ohne Verhalten). */
interface Geruest {
  karte: HTMLElement;
  kopf: HTMLButtonElement;
  kopfZeichen: HTMLElement;
  kurz: HTMLElement;
  umschalter: HTMLElement;
  feld: HTMLElement;
  zeilen: HTMLElement;
  schritt: HTMLElement;
  /** Liegt BEWUSST neben der Karte, nicht in ihr: die Karte ist oft `hidden`, und was in einem verborgenen Bereich steht, sagt keiner an. */
  ansage: HTMLElement;
}

function baueGeruest(doc: Document): Geruest {
  const karte = doc.createElement("section");
  karte.id = ENTWICKLUNG_KARTE_ID;
  karte.className = "entw-karte";
  karte.hidden = true;
  karte.setAttribute("aria-label", "Entwicklung: laufende Arbeiten");

  const kopf = doc.createElement("button");
  kopf.type = "button";
  kopf.className = "entw-kopf";
  kopf.setAttribute("aria-expanded", "false");
  kopf.setAttribute("aria-controls", FELD_ID);

  const kopfZeichen = doc.createElement("span");
  kopfZeichen.className = "entw-zeichen entw-kopfzeichen";
  kopfZeichen.setAttribute("aria-hidden", "true");

  const kopfText = doc.createElement("span");
  kopfText.className = "entw-text";
  const titel = doc.createElement("span");
  titel.className = "entw-titel";
  titel.textContent = "Entwicklung";
  const kurz = doc.createElement("span");
  kurz.className = "entw-kurz";
  kopfText.append(titel, doc.createTextNode(" "), kurz);

  const umschalter = doc.createElement("span");
  umschalter.className = "entw-umschalter";
  umschalter.setAttribute("aria-hidden", "true");

  kopf.append(kopfZeichen, kopfText, umschalter);

  const feld = doc.createElement("div");
  feld.id = FELD_ID;
  feld.className = "entw-feld";
  feld.hidden = true;
  const zeilen = doc.createElement("ul");
  zeilen.className = "entw-zeilen";
  // Mit `list-style: none` nimmt Safari/VoiceOver der Liste die Rolle — die Rolle stellt sie wieder her.
  zeilen.setAttribute("role", "list");
  zeilen.setAttribute("aria-label", "Arbeiten");
  const schritt = doc.createElement("p");
  schritt.className = "entw-schritt";
  feld.append(zeilen, schritt);

  karte.append(kopf, feld);

  const ansage = doc.createElement("p");
  ansage.className = "entw-ansage";
  ansage.setAttribute("aria-live", "polite");
  ansage.setAttribute("aria-atomic", "true");

  return { karte, kopf, kopfZeichen, kurz, umschalter, feld, zeilen, schritt, ansage };
}

/** Baut die Karte VOR `vor` (dem Chat-Verlauf) in `wurzel` ein. Verborgen, bis Arbeit läuft. */
export function baueEntwicklungKarte(wurzel: HTMLElement, vor: HTMLElement | null): EntwicklungKarte {
  const doc = wurzel.ownerDocument;
  const { karte, kopf, kopfZeichen, kurz, umschalter, feld, zeilen, schritt, ansage } = baueGeruest(doc);
  const platz = vor?.parentElement === wurzel ? vor : wurzel.firstChild;
  wurzel.insertBefore(karte, platz);
  wurzel.insertBefore(ansage, platz);

  let posten: Posten[] = [];
  let geoeffnet = false;
  let schonGezeigt = false;
  let letzteKurz = "";
  let zeilenSchluessel = "";
  let kopfZustand = "";
  let letzterLauf = 0;
  let wartend: ReturnType<typeof setTimeout> | null = null;
  let verfall: ReturnType<typeof setTimeout> | null = null;
  let takt: ReturnType<typeof setInterval> | null = null;
  let unbestaetigt: ReturnType<typeof setTimeout> | null = null;

  /** Zeichnet ein Zeichen in `ziel` — nur, wenn es sich geändert hat. Zustand ODER Name aus dem Vorrat (plus, minus). */
  const setzeZeichen = (ziel: HTMLElement, art: KartenZustand | "plus" | "minus"): void => {
    if (ziel.dataset.zeichen === art) return;
    ziel.dataset.zeichen = art;
    ziel.innerHTML = art === "plus" || art === "minus" ? zeichen(art, ZEICHEN_GROESSE) : zustandZeichen(art, ZEICHEN_GROESSE);
  };

  const baueZeile = (p: Posten): HTMLLIElement => {
    const zustand = kartenZustand(p.stufe);
    const li = doc.createElement("li");
    li.className = "entw-zeile";
    li.dataset.zustand = zustand;
    const z = doc.createElement("span");
    z.className = "entw-zeichen";
    z.dataset.zustand = zustand;
    z.setAttribute("aria-hidden", "true");
    setzeZeichen(z, zustand);
    const was = doc.createElement("span");
    was.className = "entw-was";
    const wort = doc.createElement("span");
    wort.className = "entw-nur-sr";
    wort.textContent = `${SCHMIED_ARBEIT_STUFE_NAME[p.stufe]}: `;
    was.append(wort, doc.createTextNode(p.stichwort));
    li.append(z, was);
    return li;
  };

  /** Steht der Fokus in der Karte, die gleich verschwindet, geht er an die Ansicht (Tafel-Muster) — sonst fiele er auf den Seitenanfang. */
  const fokusAbgeben = (): void => {
    if (!karte.contains(doc.activeElement)) return;
    if (!wurzel.hasAttribute("tabindex")) {
      wurzel.setAttribute("tabindex", "-1");
      wurzel.addEventListener("blur", () => wurzel.removeAttribute("tabindex"), { once: true });
    }
    wurzel.focus({ preventScroll: true });
  };

  const halteTakt = (): void => {
    if (takt) clearInterval(takt);
    takt = null;
  };
  const sichtbar = (): boolean => doc.visibilityState !== "hidden";

  const zeichne = (): void => {
    letzterLauf = Date.now();
    const aktiv = posten.filter((p) => istAktiv(p.stufe));
    if (aktiv.length === 0) {
      // Nichts läuft mehr: Karte weg, beim nächsten Mal wieder zugeklappt. Waren Arbeiten fertig geworden, sagt das die Ansage.
      if (letzteKurz !== "" && posten.some((p) => p.stufe === "fertig")) ansage.textContent = "Entwicklung: alles fertig";
      letzteKurz = "";
      geoeffnet = false;
      fokusAbgeben();
      karte.hidden = true;
      feld.hidden = true;
      kopf.setAttribute("aria-expanded", "false");
      zeilen.replaceChildren();
      zeilenSchluessel = "";
      halteTakt();
      return;
    }
    const jetzt = Date.now();
    const geordnet = ordneZeilen(posten);
    karte.hidden = false;
    if (aktiv.some((p) => p.stufe === "wartet")) karte.dataset.wartet = "1";
    else delete karte.dataset.wartet;

    const k = kurzzeile(aktiv.map((p) => p.stufe));
    if (kurz.textContent !== k) kurz.textContent = k;
    if (k !== letzteKurz) {
      // Nur eine ÄNDERUNG wird angesagt — nicht der erste Stand beim Laden und nicht jede Minute.
      if (schonGezeigt) ansage.textContent = `Entwicklung: ${k}`;
      letzteKurz = k;
    }
    schonGezeigt = true;

    const laeuft = aktiv.some((p) => p.stufe !== "wartet");
    const neuerKopf: KartenZustand = laeuft ? "laeuft" : "offen";
    if (neuerKopf !== kopfZustand) {
      kopfZustand = neuerKopf;
      kopfZeichen.dataset.zustand = neuerKopf;
      setzeZeichen(kopfZeichen, neuerKopf);
    }
    setzeZeichen(umschalter, geoeffnet ? "minus" : "plus");
    kopf.setAttribute("aria-expanded", geoeffnet ? "true" : "false");
    feld.hidden = !geoeffnet;

    const schluessel = geordnet.map((p) => `${p.id}:${p.stufe}:${p.stichwort}`).join("|");
    if (schluessel !== zeilenSchluessel) {
      zeilenSchluessel = schluessel;
      zeilen.replaceChildren(...geordnet.map(baueZeile));
    }
    const s = schrittSatz(posten, jetzt);
    if (schritt.textContent !== s) schritt.textContent = s;

    takt ??= setInterval(() => {
      if (sichtbar()) zeichne();
    }, KARTE_TAKT_MS);
  };

  /** Sofort, wenn die letzte Zeichnung lange her ist; sonst einmal nachgeholt — so zeichnet ein Schwall von Ständen nur zweimal. */
  const plane = (): void => {
    const seit = Date.now() - letzterLauf;
    if (seit >= KARTE_DROSSEL_MS) {
      zeichne();
      return;
    }
    wartend ??= setTimeout(() => {
      wartend = null;
      zeichne();
    }, KARTE_DROSSEL_MS - seit);
  };

  const zeige = (stand: SchmiedArbeitStand): void => {
    if (unbestaetigt) clearTimeout(unbestaetigt);
    unbestaetigt = null;
    const jetzt = Date.now();
    const echte = stand.arbeiten.filter((a) => a.id !== SCHMIED_MARKE_ID).slice(0, SCHMIED_ARBEIT_HOECHSTENS);
    posten = echte.map((a) => ({
      id: a.id,
      stichwort: a.stichwort,
      stufe: a.stufe,
      start: jetzt - a.seitMs,
      ...(a.stufe === "fertig" ? { dauerMs: a.seitMs } : {}),
    }));
    // Verfällt auch hier von selbst, wenn der Server nach einem Abriss kein Ende mehr schicken konnte.
    if (verfall) clearTimeout(verfall);
    verfall = null;
    const laufende = echte.filter((a) => istAktiv(a.stufe));
    if (laufende.length > 0) {
      const rest = Math.max(0, Math.min(...laufende.map((a) => SCHMIED_ARBEIT_VERFALL_MS - a.seitMs)));
      verfall = setTimeout(() => zeige({ arbeiten: [] }), rest);
    }
    plane();
  };

  const beimZurueckkehren = (): void => {
    if (sichtbar() && !karte.hidden) zeichne();
  };
  doc.addEventListener("visibilitychange", beimZurueckkehren);

  kopf.addEventListener("click", () => {
    geoeffnet = !geoeffnet;
    zeichne();
  });

  return {
    zeige,
    verbindungNeu() {
      // Läuft noch etwas, schickt der Server den Stand gleich nach (ein leerer wird nicht geschickt): bleibt das aus, ist es zu Ende.
      if (posten.length === 0 || unbestaetigt) return;
      unbestaetigt = setTimeout(() => zeige({ arbeiten: [] }), KARTE_NACHLIEFERUNG_MS);
    },
    stopp() {
      halteTakt();
      for (const uhr of [wartend, verfall, unbestaetigt]) if (uhr) clearTimeout(uhr);
      wartend = null;
      verfall = null;
      unbestaetigt = null;
      doc.removeEventListener("visibilitychange", beimZurueckkehren);
    },
  };
}
